import type { _RouteRecordProps, RouteLocationNormalized, RouteLocationRaw, RouterOptions } from 'vue-router';
import { createWebHistory } from 'vue-router';

import { RouteNames } from '@/models/enums/RouteNames';
import { Role } from '@sysarb/sysarb-types/service-base/enums';
import { jobArchitectureRoutes } from '@/views/jobArchitecture/jobArchitectureRoutes';
import { useLabsStore } from '@/views/jobArchitecture/composables/useLabs';
import { dataImportRoutes } from '@/views/dataImport/dataImportRoutes';
import { isAnalysisType } from '@/views/payEquityAudit/analyzeV2/guards';
import { useSettings } from '@/login/composables/useSettings';
import { usePackage } from './composables/usePackage';
import { getUserTypeStartPageRoute } from './composables/useRouterHelper';
import { useLoginStore } from '@/login/stores/useLoginStore';
import { CustomizationInvolveViewIdentifier } from '@sysarb/sysarb-types/general/enums/customEmployment';

declare module 'vue-router' {
    interface RouteMeta {
        // is optional
        requiresAuth: boolean
        // must be declared by every route
        userTypes?: Role[]
    }
}

// Maps a customization view identifier to the employee custom-dashboard route it should use (or null for the default flow).
function customInvolveEmployeeDashboard(identifier: CustomizationInvolveViewIdentifier | null | undefined): RouteNames | null {
    if (identifier === CustomizationInvolveViewIdentifier.HIT_INVOLVE_001) return RouteNames.EmployeeCustomEmploymentDashboard;
    if (identifier === CustomizationInvolveViewIdentifier.HIT_INVOLVE_DEAI_001) return RouteNames.EmployeeDeaiEmploymentDashboard;
    return null;
}

const MANAGER_EMPLOYEE_ACTIVITIES_ROUTES: readonly string[] = [
    RouteNames.ManagerEmployeeActivities,
    RouteNames.ManagerEmployeeDiscussions,
    RouteNames.ManagerEmployeeDiscussion,
    RouteNames.ManagerEmployeeTargets,
    RouteNames.ManagerEmployeeAssessments,
];

function isActivitiesRoute(names: readonly string[], name: RouteLocationNormalized['name']): boolean {
    return typeof name === 'string' && names.includes(name);
}

const routerOptions: RouterOptions = {
    history: createWebHistory(),
    routes: [
        { // Redirect the bare root to the login flow.
            path: '',
            redirect: { name: RouteNames.Login },
        },
        { // Login flow (stages rendered via the nested router-view in LoginView)
            path: '/login',
            component: async () => import('./views/newlogin/LoginView.vue'),
            meta: {
                requiresAuth: false,
            },
            children: [
                {
                    path: '',
                    name: RouteNames.Login,
                    component: async () => import('./views/newlogin/stages/UsernameStage.vue'),
                    meta: { requiresAuth: false },
                },
                {
                    path: 'password',
                    name: RouteNames.LoginPassword,
                    component: async () => import('./views/newlogin/stages/PasswordStage.vue'),
                    meta: { requiresAuth: false },
                },
                {
                    path: 'sso-select',
                    name: RouteNames.LoginSsoSelect,
                    component: async () => import('./views/newlogin/stages/SsoSelectStage.vue'),
                    meta: { requiresAuth: false },
                },
                {
                    path: 'bankid',
                    name: RouteNames.LoginBankId,
                    component: async () => import('./views/newlogin/components/BankId.vue'),
                    meta: { requiresAuth: false },
                },
            ],
        },
        { // Select role
            path: '/select-role',
            name: RouteNames.SelectRole,
            component: async () => import('./views/SelectRole.vue'),
            props: true,
            meta: {
                requiresAuth: true,
                userTypes: [
                    Role.Administrator,
                    Role.SystemAdministrator,
                    Role.Consultant,
                    Role.Employee,
                    Role.Manager,
                    Role.ManagerDeputy,
                    Role.OperationalAdministrator,
                    Role.ReadOnly,
                    Role.Root,
                    Role.SalarySurveyAnalyst,
                    Role.SectionManager,
                    Role.Union
                ],
            },
        },
        { // My user
            path: '/my-user',
            name: RouteNames.MyUser,
            component: async () => import('./views/myUser/MyUser.vue'),
            props: true,
            meta: {
                requiresAuth: true,
                userTypes: [
                    Role.Administrator,
                    Role.SystemAdministrator,
                    Role.Consultant,
                    Role.Employee,
                    Role.Manager,
                    Role.ManagerDeputy,
                    Role.OperationalAdministrator,
                    Role.ReadOnly,
                    Role.Root,
                    Role.SalarySurveyAnalyst,
                    Role.SectionManager,
                    Role.Union
                ],
            },
        },
        { // Change password
            path: '/change-password',
            name: RouteNames.LoginChangePassword,
            component: async () => import('./views/login/ChangePassword.vue'),
            props: true,
            meta: {
                requiresAuth: true,
                userTypes: [
                    Role.Administrator,
                    Role.SystemAdministrator,
                    Role.Consultant,
                    Role.Employee,
                    Role.Manager,
                    Role.ManagerDeputy,
                    Role.OperationalAdministrator,
                    Role.ReadOnly,
                    Role.Root,
                    Role.SalarySurveyAnalyst,
                    Role.SectionManager,
                    Role.Union
                ],
            },
        },
        { // SSO login
            path: '/sso/:id',
            name: RouteNames.SSOLogin,
            component: async () => import('./views/login/SSO.vue'),
            props: true,
            meta: {
                requiresAuth: false,
            },
        },
        { // Fortnox auth
            path: '/fortnox-auth',
            name: RouteNames.FortnoxAuth,
            component: async () => import('./views/dataImport/integration/integrations/FortnoxAuth.vue'),
            props: true,
            meta: {
                requiresAuth: true,
                userTypes: [Role.Administrator, Role.SystemAdministrator],
            },
        },
        { // Reset password
            path: '/set-password',
            name: RouteNames.SetPassword,
            component: async () => import('./views/login/SetPassword.vue'),
            meta: {
                requiresAuth: false,
            },
        },
        { // Verify email
            path: '/verify-email',
            name: RouteNames.VerifyEmail,
            component: async () => import('./views/login/VerifyEmail.vue'),
            meta: {
                requiresAuth: true,
            },
        },
        { // No module access
            // Where every guard sends a user whose package doesn't include the portal they asked
            // for. Open to all roles on purpose: the page states that the module isn't available
            // and keeps the normal chrome, so the user can still switch role or log out.
            path: '/no-access',
            name: RouteNames.NoModuleAccess,
            component: async () => import('./views/noAccess/NoModuleAccess.vue'),
            meta: {
                requiresAuth: true,
            },
        },
        { // Custom employment
            path: '/custom-employment',
            component: async () => import('./views/customEmployment/CustomEmploymentView.vue'),
            beforeEnter: (to: RouteLocationNormalized): { name: string } | undefined => {
                const { customizationSettings } = useSettings();
                const { enableInvolvePortalEmployee } = usePackage();

                if (enableInvolvePortalEmployee.value !== true) {
                    return { name: RouteNames.NoModuleAccess };
                }
                const customDashboard = customInvolveEmployeeDashboard(customizationSettings.value?.customizationEmploymentViewIdentifier);
                if (customDashboard != null) {
                    if (to.name === customDashboard) return undefined;
                    return { name: customDashboard };
                }
                return { name: RouteNames.EmployeeDashboard };
            },
            meta: {
                requiresAuth: true,
                userTypes: [Role.Employee]
            },
            children: [
                {
                    path: ':employmentId?',
                    component: async () => import('./views/customEmployment/EmploymentView.vue'),
                    props: true,
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.Employee]
                    },
                    children: [
                        {
                            path: '',
                            name: RouteNames.EmployeeCustomEmploymentDashboard,
                            component: async () => import('./views/customEmployment/pages/Dashboard.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Employee]
                            },
                        },
                    ]
                }
            ]
        },
        { // Employment
            path: '/employment',
            /*
             * product-sysarb emails link to /employee-v2/activities/discussions/discussion/:id by
             * string concatenation, and those mails are already in people's inboxes. Keep the
             * /employee-v2 redirect below working, and keep this activities sub-path spelled
             * exactly as it is.
             */
            component: async () => import('./views/employeeV2/EmployeeView.vue'),
            beforeEnter: (to: RouteLocationNormalized): { name: string } | undefined => {
                const { customizationSettings } = useSettings();
                const { enableInvolvePortalEmployee, payManagementOnly } = usePackage();
                if (payManagementOnly.value === true) {
                    return undefined;
                }
                if (enableInvolvePortalEmployee.value === true) {
                    const customDashboard = customInvolveEmployeeDashboard(customizationSettings.value?.customizationEmploymentViewIdentifier);
                    if (customDashboard != null) {
                        if (to.name === customDashboard) return undefined;
                        return { name: customDashboard };
                    }
                    return undefined;
                } else {
                    return { name: RouteNames.NoModuleAccess };
                }
            },
            meta: {
                requiresAuth: true,
                userTypes: [Role.Employee]
            },
            children: [
                {
                    path: '',
                    name: RouteNames.Employee,
                    component: async () => import('./views/employmentV3/pages/EmployeeView.vue'),
                    meta: {
                        requiresAuth: true
                    },
                    children: [
                        {
                            path: '',
                            name: RouteNames.EmployeeDashboard,
                            component: async () => import('./views/employmentV3/pages/EmploymentDashboardSections.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Employee]
                            },
                            beforeEnter: (): { name: string } | undefined => {
                                // Pay Management has no dashboard; activities is the whole portal.
                                const { payManagementOnly } = usePackage();
                                if (payManagementOnly.value === true) {
                                    return { name: RouteNames.EmployeeActivities };
                                }
                                return undefined;
                            },
                        },
                        {
                            path: 'deai',
                            name: RouteNames.EmployeeDeaiEmploymentDashboard,
                            component: async () => import('./views/customEmployment/deai/DeaiDashboard.vue'),
                            meta: {
                                requiresAuth: true
                            },
                        },
                        {
                            path: 'activities',
                            name: RouteNames.EmployeeActivities,
                            component: async () => import('./views/employeeV2/pages/Activities.vue'),
                            redirect: { name: RouteNames.EmployeeDiscussions },
                            meta: {
                                requiresAuth: true
                            },
                            children: [
                                {
                                    path: 'discussions',
                                    name: RouteNames.EmployeeDiscussions,
                                    component: async () => import('./views/employeeV2/pages/EmployeeDiscussions.vue'),
                                    children: [
                                        {
                                            path: 'discussion/:id',
                                            name: RouteNames.EmployeeDiscussion,
                                            component: async () => import('./views/employeeV2/pages/EmploymentDiscussion.vue'),
                                            props: true,
                                        },
                                    ]
                                },
                                {
                                    path: 'targets',
                                    name: RouteNames.EmployeeTargets,
                                    component: async () => import('./views/employeeV2/pages/EmployeeTargets.vue'),
                                },
                                {
                                    path: 'assessments',
                                    name: RouteNames.EmployeeAssessments,
                                    component: async () => import('./views/employeeV2/pages/EmployeeAssessments.vue'),
                                },
                            ]
                        },
                    ]
                },
            ]
        },
        {
            path: '/employee-v2/:rest(.*)*',
            redirect: to => to.fullPath.replace(/^\/employee-v2/, '/employment'),
        },
        { // Union
            path: '/union',
            component: async () => import('./views/baseView/Union.vue'),
            meta: {
                requiresAuth: true,
                userTypes: [Role.Union]
            },
            children: [
                {
                    path: '',
                    name: RouteNames.UnionStart,
                    component: async () => import('./views/dashboard/Union.vue'),
                    props: true,
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.Union]
                    },
                },
                { // Salary review
                    path: 'salary-set',
                    component: async () => import('./views/SalaryReview.vue'),
                    props: true,
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.Union]
                    },
                    children: [
                        {
                            path: '',
                            name: RouteNames.UnionSalarySetDashboard,
                            component: async () => import('./views/manager/ManagerSalarySetDashboard.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Union]
                            },
                        },
                        {
                            path: ':id',
                            name: RouteNames.UnionSalarySet,
                            component: async () => import('./views/union/UnionSalarySet.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Union]
                            },
                        },
                    ]
                },
                {
                    path: 'job-architecture',
                    component: async () => import('./views/jobArchitecturePreview/JobArchitecturePreview.vue'),
                    props: true,
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.Union]
                    },
                    children: [
                        {
                            path: '',
                            name: RouteNames.UnionJobArchitectureOverview,
                            component: async () => import('./views/jobArchitecturePreview/overview/Overview.vue'),
                        },
                    ]
                },
                {
                    path: 'external-job-architecture',
                    component: async () => import('./views/externalJobArchitecture/preview/ExternalJobArchitecturePreview.vue'),
                    props: true,
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.Union]
                    },
                    children: [
                        {
                            path: '',
                            name: RouteNames.UnionExternalJobArchitectureOverview,
                            component: async () => import('./views/externalJobArchitecture/ExternalOverview.vue'),
                        },
                    ]
                },
            ]
        },
        { // Section manager v2
            path: '/section-manager-v2',
            redirect: '/section-manager-v2/dashboard',
            component: async () => import('./views/sectionManager/SectionManager.vue'),
            meta: {
                requiresAuth: true,
                userTypes: [Role.SectionManager]
            },
            beforeEnter: (to: RouteLocationNormalized): { name: string } | undefined => {
                const { customizationSettings } = useSettings();
                const { enableInvolvePortalManager } = usePackage();

                // HIT customers run custom manager/employee dashboards with no
                // section manager variant — section managers get an empty start page.
                if (customizationSettings.value?.customizationEmploymentViewIdentifier === CustomizationInvolveViewIdentifier.HIT_INVOLVE_001) {
                    if (to.name === RouteNames.SectionManagerStart) return undefined;
                    return { name: RouteNames.SectionManagerStart };
                }
                if (enableInvolvePortalManager.value !== true) {
                    return { name: RouteNames.NoModuleAccess };
                }
                return undefined;
            },
            children: [
                {
                    path: 'start',
                    name: RouteNames.SectionManagerStart,
                    component: async () => import('./views/sectionManager/pages/EmptyStart.vue'),
                    meta: {
                        requiresAuth: true
                    }
                },
                {
                    path: 'dashboard',
                    name: RouteNames.SectionManagerDashboardV2,
                    component: async () => import('./views/sectionManager/pages/Dashboard.vue'),
                    meta: {
                        requiresAuth: true
                    }
                },
                {
                    path: 'analysis',
                    name: RouteNames.SectionManagerAnalysisV2,
                    component: async () => import('./views/sectionManager/pages/Analysis.vue'),
                    meta: {
                        requiresAuth: true
                    }
                },
                {
                    path: 'employment/:employmentId/:employeeId',
                    component: async () => import('./views/employmentV3/pages/ManagerEmployeeView.vue'),
                    beforeEnter: (): { name: string } | undefined => {
                        // Pay Management package: section managers work at dashboard and salary
                        // review level; individual employee pages are manager-only there.
                        const { payManagementOnly } = usePackage();
                        if (payManagementOnly.value === true) {
                            return { name: RouteNames.SectionManagerDashboardV2 };
                        }
                        return undefined;
                    },
                    meta: {
                        requiresAuth: true
                    },
                    children: [
                        {
                            path: '',
                            name: RouteNames.SectionManagerEmployeeDashboard,
                            component: async () => import('./views/employmentV3/pages/EmploymentDashboardSections.vue'),
                            meta: {
                                requiresAuth: true
                            },
                        },
                        {
                            path: 'activities',
                            name: RouteNames.SectionManagerEmployeeActivities,
                            component: async () => import('./views/managerV2/pages/ManagerEmployeeActivities.vue'),
                            redirect: { name: RouteNames.SectionManagerEmployeeDiscussions },
                            props: true,
                            meta: {
                                requiresAuth: true
                            },
                            children: [
                                {
                                    path: 'discussions',
                                    name: RouteNames.SectionManagerEmployeeDiscussions,
                                    props: true,
                                    component: async () => import('./views/managerV2/pages/ManagerEmployeeDiscussions.vue'),
                                    children: [
                                        {
                                            path: 'employee-discussion/:id',
                                            name: RouteNames.SectionManagerEmployeeDiscussion,
                                            component: async () => import('./views/managerV2/pages/ManagerEmploymentDiscussion.vue'),
                                            props: true,
                                        },
                                    ]
                                },
                                {
                                    path: 'targets',
                                    name: RouteNames.SectionManagerEmployeeTargets,
                                    component: async () => import('./views/employeeV2/pages/EmployeeTargets.vue'),
                                },
                                {
                                    path: 'assessments',
                                    name: RouteNames.SectionManagerEmployeeAssessments,
                                    props: true,
                                    component: async () => import('./views/managerV2/pages/ManagerEmployeeAssessments.vue'),
                                },
                            ]
                        },
                    ]
                },
                {
                    path: 'employee/:employmentId/:employeeId/:rest(.*)*',
                    redirect: to => to.fullPath.replace(
                        /^\/section-manager-v2\/employee\//,
                        '/section-manager-v2/employment/',
                    ),
                },
                { // Salary review
                    path: 'salary-set',
                    component: async () => import('./views/SalaryReview.vue'),
                    props: true,
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.SectionManager]
                    },
                    children: [
                        {
                            path: '',
                            name: RouteNames.SectionManagerSalarySetDashboard,
                            component: async () => import('./views/manager/ManagerSalarySetDashboard.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.SectionManager]
                            },
                        },
                        {
                            path: ':id',
                            name: RouteNames.SectionManagerSalarySet,
                            component: async () => import('./views/manager/ManagerSalarySet.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.SectionManager]
                            },
                        }
                    ]
                },
                {
                    path: 'job-architecture',
                    component: async () => import('./views/jobArchitecturePreview/JobArchitecturePreview.vue'),
                    props: true,
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.SectionManager]
                    },
                    children: [
                        {
                            path: '',
                            name: RouteNames.SectionManagerJobArchitectureOverview,
                            component: async () => import('./views/jobArchitecturePreview/overview/Overview.vue'),
                        },
                    ]
                },
                {
                    path: 'external-job-architecture',
                    component: async () => import('./views/externalJobArchitecture/preview/ExternalJobArchitecturePreview.vue'),
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.SectionManager]
                    },
                    children: [
                        {
                            path: '',
                            name: RouteNames.SectionManagerExternalJobArchitectureOverview,
                            component: async () => import('./views/externalJobArchitecture/ExternalOverview.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.SectionManager]
                            },
                        },
                    ]
                },
            ]
        },
        { // Manager v2
            path: '/manager-v2',
            redirect: '/manager-v2/dashboard',
            component: async () => import('./views/managerV2/Manager.vue'),
            meta: {
                requiresAuth: true,
                userTypes: [Role.Manager]
            },
            beforeEnter: (): { name: string } | undefined => {
                const { enableInvolvePortalManager } = usePackage();

                if (enableInvolvePortalManager.value !== true) {
                    return { name: RouteNames.NoModuleAccess };
                }
                return undefined;
            },
            children: [
                {
                    path: 'dashboard',
                    name: RouteNames.ManagerDashboardV2,
                    component: async () => import('./views/managerV2/pages/Dashboard.vue'),
                    meta: {
                        requiresAuth: true
                    }
                },
                {
                    path: 'analysis',
                    name: RouteNames.ManagerAnalysisV2,
                    component: async () => import('./views/managerV2/pages/Analysis.vue'),
                    meta: {
                        requiresAuth: true
                    }
                },
                {
                    path: 'discussions',
                    name: RouteNames.ManagerDiscussions,
                    component: async () => import('./views/managerV2/pages/Discussions.vue'),
                    beforeEnter: (): { name: string } | undefined => {
                        const { enableDiscussions } = usePackage();

                        if (enableDiscussions.value === true) {
                            return undefined;
                        }
                        return { name: RouteNames.ManagerDashboardV2 };
                    },
                    meta: {
                        requiresAuth: true
                    },
                    children: [
                        {
                            path: 'edit-discussion/one-on-one/:id',
                            name: RouteNames.ManagerDiscussionEditOneOnOne,
                            component: async () => import('./views/managerV2/pages/EditOneOnOneDiscussion.vue'),
                            props: true,
                        },
                        {
                            path: 'edit-discussion/group/:id',
                            name: RouteNames.ManagerDiscussionEditGroup,
                            component: async () => import('./views/managerV2/pages/EditGroupDiscussion.vue'),
                            props: true,
                        },
                        {
                            path: 'employee-discussion/:id',
                            name: RouteNames.ManagerDiscussionEmployment,
                            component: async () => import('./views/managerV2/pages/ManagerEmploymentDiscussion.vue'),
                            props: true,
                        },
                    ]
                },
                {
                    path: 'job-architecture',
                    component: async () => import('./views/jobArchitecturePreview/JobArchitecturePreview.vue'),
                    props: true,
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.Manager]
                    },
                    children: [
                        {
                            path: '',
                            name: RouteNames.ManagerJobArchitectureOverviewV2,
                            component: async () => import('./views/jobArchitecturePreview/overview/Overview.vue'),
                        },
                    ]
                },
                {
                    path: 'external-job-architecture',
                    component: async () => import('./views/externalJobArchitecture/preview/ExternalJobArchitecturePreview.vue'),
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.Manager]
                    },
                    children: [
                        {
                            path: '',
                            name: RouteNames.ManagerExternalJobArchitectureOverviewV2,
                            component: async () => import('./views/externalJobArchitecture/ExternalOverview.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Manager]
                            },
                        },
                    ]
                },
                {
                    path: 'targets',
                    name: RouteNames.ManagerTargets,
                    component: async () => import('./views/managerV2/pages/Targets.vue'),
                    beforeEnter: (): { name: string } | undefined => {
                        const { enableTargets } = usePackage();

                        if (enableTargets.value === true) {
                            return undefined;
                        }
                        return { name: RouteNames.ManagerDashboardV2 };
                    },
                    meta: {
                        requiresAuth: true
                    }
                },
                {
                    path: 'c-employment/:employmentId',
                    component: async () => import('./views/customEmployment/EmploymentView.vue'),
                    props: true,
                    beforeEnter: (to: RouteLocationNormalized): { name: string } | undefined => {
                        const { customizationSettings } = useSettings();
                        const { enableInvolvePortalEmployee } = usePackage();

                        if (enableInvolvePortalEmployee.value === true) {
                            if(customizationSettings.value?.customizationEmploymentViewIdentifier === CustomizationInvolveViewIdentifier.HIT_INVOLVE_001) {
                                if (to.name === RouteNames.ManagerCustomEmploymentDashboard) return undefined;
                                return { name: RouteNames.ManagerCustomEmploymentDashboard };
                            }
                            return { name: RouteNames.ManagerEmployeeDashboard };
                        }
                        return { name: RouteNames.ManagerDashboardV2 };
                    },
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.Manager]
                    },
                    children: [
                        {
                            path: '',
                            name: RouteNames.ManagerCustomEmploymentDashboard,
                            component: async () => import('./views/customEmployment/pages/Dashboard.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Manager]
                            },
                        },
                    ]
                },
                {
                    path: 'employment/:employmentId/:employeeId',
                    component: async () => import('./views/employmentV3/pages/ManagerEmployeeView.vue'),
                    props: true,
                    beforeEnter: (to: RouteLocationNormalized): { name: string; params?: RouteLocationNormalized['params'] } | undefined => {
                        const { customizationSettings } = useSettings();
                        const { enableInvolvePortalEmployee, payManagementOnly } = usePackage();

                        if (payManagementOnly.value === true) {
                            // Pay Management managers get the activities pages only, not the dashboard.
                            if (isActivitiesRoute(MANAGER_EMPLOYEE_ACTIVITIES_ROUTES, to.name)) return undefined;
                            return { name: RouteNames.ManagerEmployeeActivities, params: to.params };
                        }

                        if (enableInvolvePortalEmployee.value === true) {
                            if (customizationSettings.value?.customizationEmploymentViewIdentifier === CustomizationInvolveViewIdentifier.HIT_INVOLVE_001) {
                                return { name: RouteNames.ManagerCustomEmploymentDashboard, params: { employmentId: to.params.employmentId } };
                            }
                            return undefined;
                        }
                        return { name: RouteNames.ManagerDashboardV2 };
                    },
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.Manager, Role.SectionManager]
                    },
                    children: [
                        {
                            path: '',
                            name: RouteNames.ManagerEmployeeDashboard,
                            component: async () => import('./views/employmentV3/pages/EmploymentDashboardSections.vue'),
                            meta: {
                                requiresAuth: true
                            },
                            // On the index rather than the parent, so the back button from activities is caught too.
                            beforeEnter: (to: RouteLocationNormalized): { name: string; params: RouteLocationNormalized['params'] } | undefined => {
                                const { customizationSettings } = useSettings();
                                if (customizationSettings.value?.customizationEmploymentViewIdentifier === CustomizationInvolveViewIdentifier.HIT_INVOLVE_DEAI_001) {
                                    return { name: RouteNames.ManagerDeaiEmploymentDashboard, params: to.params };
                                }
                                return undefined;
                            },
                        },
                        {
                            path: 'deai',
                            name: RouteNames.ManagerDeaiEmploymentDashboard,
                            component: async () => import('./views/employeeV2/pages/Dashboard.vue'),
                            meta: {
                                requiresAuth: true
                            },
                            beforeEnter: (to: RouteLocationNormalized): { name: string; params: RouteLocationNormalized['params'] } | undefined => {
                                const { customizationSettings } = useSettings();
                                if (customizationSettings.value?.customizationEmploymentViewIdentifier !== CustomizationInvolveViewIdentifier.HIT_INVOLVE_DEAI_001) {
                                    return { name: RouteNames.ManagerEmployeeDashboard, params: to.params };
                                }
                                return undefined;
                            },
                        },
                        {
                            path: 'activities',
                            name: RouteNames.ManagerEmployeeActivities,
                            component: async () => import('./views/managerV2/pages/ManagerEmployeeActivities.vue'),
                            redirect: { name: RouteNames.ManagerEmployeeDiscussions },
                            props: true,
                            children: [
                                {
                                    path: 'discussions',
                                    name: RouteNames.ManagerEmployeeDiscussions,
                                    props: true,
                                    component: async () => import('./views/managerV2/pages/ManagerEmployeeDiscussions.vue'),
                                    children: [
                                        {
                                            path: 'employee-discussion/:id',
                                            name: RouteNames.ManagerEmployeeDiscussion,
                                            component: async () => import('./views/managerV2/pages/ManagerEmploymentDiscussion.vue'),
                                            props: true,
                                        },
                                    ]
                                },
                                {
                                    path: 'targets',
                                    name: RouteNames.ManagerEmployeeTargets,
                                    component: async () => import('./views/employeeV2/pages/EmployeeTargets.vue'),
                                },
                                {
                                    path: 'assessments',
                                    name: RouteNames.ManagerEmployeeAssessments,
                                    props: true,
                                    component: async () => import('./views/managerV2/pages/ManagerEmployeeAssessments.vue'),
                                },
                            ]
                        },
                    ]
                },
                { // Salary review
                    path: 'salary-set',
                    component: async () => import('./views/SalaryReview.vue'),
                    props: true,
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.Manager]
                    },
                    children: [
                        {
                            path: '',
                            name: RouteNames.ManagerSalarySetDashboardV2,
                            component: async () => import('./views/manager/ManagerSalarySetDashboard.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Manager]
                            },
                        },
                        {
                            path: ':id',
                            name: RouteNames.ManagerSalarySetV2,
                            component: async () => import('./views/manager/ManagerSalarySet.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Manager]
                            },
                        },
                    ]
                },
                { // Salary review assessment
                    path: 'salary-review-assessment',
                    component: async () => import('./views/SalaryReview.vue'),
                    props: true,
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.Manager]
                    },
                    children: [
                        {
                            path: '',
                            name: RouteNames.ManagerSalaryReviewAssessmentDashboardV2,
                            component: async () => import('./views/manager/ManagerSalaryReviewAssessmentDashboard.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Manager]
                            },
                        },
                        {
                            path: ':id/:salaryReviewEmploymentId?',
                            name: RouteNames.ManagerSalaryReviewAssessmentV2,
                            component: async () => import('./views/manager/ManagerSalaryReviewAssessment.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Manager]
                            },
                        }
                    ]
                },
            ]
        },
        { // Administrator
            path: '/administrator',
            component: async () => import('./views/baseView/Administrator.vue'),
            meta: {
                requiresAuth: true,
                userTypes: [
                    Role.Administrator,
                    Role.Root,
                    Role.SystemAdministrator,
                    Role.Consultant,
                    Role.SalarySurveyAnalyst
                ]
            },
            children: [
                {
                    path: 'welcome-to-sysarb',
                    name: RouteNames.AdministratorWelcomeToSysarb,
                    component: async () => import('./views/WelcomeToSysarb.vue'),
                    props: true,
                    meta: {
                        requiresAuth: true,
                        userTypes: [
                            Role.Administrator,
                            Role.Root,
                            Role.SystemAdministrator,
                            Role.Consultant,
                            Role.SalarySurveyAnalyst
                        ]
                    },
                },
                {
                    path: 'agent-chat',
                    name: RouteNames.AdministratorAgentChat,
                    component: async () => import('./views/agentChat/AgentChatView.vue'),
                    meta: {
                        requiresAuth: true,
                        userTypes: [
                            Role.Administrator,
                            Role.Root,
                            Role.SystemAdministrator,
                            Role.Consultant,
                            Role.SalarySurveyAnalyst
                        ]
                    },
                },
                {
                    path: 'global-overview-v2',
                    name: RouteNames.AdministratorGlobalOverviewV2,
                    component: async () => import('./views/globalOverviewV2/LandingView.vue'),
                    props: route => ({
                        salaryType: typeof route.query.salaryType === 'string' ? route.query.salaryType : undefined,
                        legalEntity: typeof route.query.legalEntity === 'string' ? route.query.legalEntity : undefined,
                        countryCode: typeof route.query.countryCode === 'string' ? route.query.countryCode : undefined,
                    }),
                    meta: {
                        requiresAuth: true,
                        userTypes: [
                            Role.Administrator,
                            Role.Root,
                            Role.SystemAdministrator,
                            Role.Consultant,
                            Role.SalarySurveyAnalyst
                        ]
                    },
                },
                { // Partner platform
                    path: 'partner-platform',
                    name: RouteNames.PartnerPlatform,
                    component: async () => import('./views/PartnerPlatform.vue'),
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                    },
                },
                { // Base registry
                    path: 'base-registry',
                    component: async () => import('./views/baseRegistry/BaseRegistry.vue'),
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                    },
                    children: [
                        {
                            path: '',
                            component: async () => import('./views/baseRegistry/BaseRegistryView.vue'),
                            children: [
                                {
                                    path: '',
                                    name: RouteNames.AdministratorBaseRegistryEmployee,
                                    component: async () => import('./views/baseRegistry/EmployeeList.vue'),
                                    meta: {
                                        requiresAuth: true,
                                        userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                                    },
                                },
                                {
                                    path: 'details/:tab/:employeeId?/:userId?/:parent?',
                                    name: RouteNames.AdministratorBaseRegistryDetails,
                                    props: router => ({
                                        id: router.params.id,
                                        employeeId: router.params.employeeId,
                                        userId: router.params.userId,
                                        tab: router.params.tab,
                                        parent: router.params.parent
                                    }),
                                    component: async () => import('./views/baseRegistry/EmployeeDetails.vue'),
                                    meta: {
                                        requiresAuth: true,
                                        userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                                    },
                                },
                            ]
                        },
                        {
                            path: 'organisation',
                            component: async () => import('./views/baseRegistry/BaseRegistryView.vue'),
                            children: [
                                {
                                    path: '',
                                    component: async () => import('./views/baseRegistry/organisation/OrganisationTree.vue'),
                                    name: RouteNames.AdministratorBaseRegistryOrganization,
                                    meta: {
                                        requiresAuth: true,
                                        userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                                    },
                                },
                                {
                                    path: ':tab/:id',
                                    name: RouteNames.AdministratorBaseRegistryOrganizationDetails,
                                    props: router => ({
                                        id: router.params.id,
                                        tab: router.params.tab,
                                        parent: router.params.parent
                                    }),
                                    component: async () => import('./views/baseRegistry/organisation/OrganisationDetails.vue'),
                                    meta: {
                                        requiresAuth: true,
                                        userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                                    },
                                },
                            ],
                        },
                        {
                            path: 'createuser',
                            name: RouteNames.AdministratorBaseRegistryCreateUser,
                            component: async () => import('./views/baseRegistry/BaseRegistryCreateExternalUser.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                        },
                        {
                            path: 'details/:tab/newEmployment/:employeeId?/:userId?',
                            name: RouteNames.AdministratorBaseRegistryCreateEmployment,
                            props: router => ({
                                employeeId: router.params.employeeId,
                                userId: router.params.userId,
                                tab: router.params.tab
                            }),
                            component: async () => import('./views/baseRegistry/BaseRegistryCreateEmployment.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                        }
                    ]
                },
                { // Settings
                    path: 'settings',
                    component: async () => import('./views/settings/SettingsDashboard.vue'),
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                    },
                    children: [
                        {
                            path: 'labs',
                            name: RouteNames.AdministratorSettings,
                            component: async () => import('./views/settings/BetaFlags.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                        },
                        {
                            path: 'security-settings',
                            name: RouteNames.AdministratorSecuritySettings,
                            component: async () => import('./views/settings/SecuritySettings.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                        },
                        {
                            path: 'salary-settings',
                            name: RouteNames.AdministratorSalarySettings,
                            component: async () => import('./views/settings/SalarySettings.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                        },
                        {
                            path: 'cost-center',
                            name: RouteNames.AdministratorCostCenter,
                            component: async () => import('./views/settings/CostCenter.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                        },
                        {
                            path: 'legal-entity',
                            name: RouteNames.AdministratorLegalEntity,
                            component: async () => import('./views/settings/LegalEntity.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                        },
                        {
                            path: 'files',
                            name: RouteNames.AdministratorFile,
                            component: async () => import('./views/settings/Files.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                        },
                        {
                            path: 'job-cluster',
                            name: RouteNames.AdministratorSettingsJobCluster,
                            component: async () => import('./views/settings/jobArchitecture/JobCluster.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                        },
                        {
                            path: 'job-families',
                            name: RouteNames.AdministratorSettingsJobFamilies,
                            component: async () => import('./views/settings/jobArchitecture/JobFamilies.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                        },
                        {
                            path: 'access',
                            name: RouteNames.AdministratorSettingsJobArchitectureAccess,
                            component: async () => import('./views/settings/jobArchitecture/Access.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                        },
                        {
                            path: 'manage-grades',
                            name: RouteNames.AdministratorExternalJobArchitectureManageGrades,
                            component: async () => import('./views/settings/jobArchitecture/ManageGrades.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                        },
                        {
                            path: 'job-architecture',
                            name: RouteNames.AdministratorSettingsJobArchitecture,
                            component: async () => import('./views/settings/jobArchitecture/JobArchitectureSettings.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                        },
                        {
                            path: 'job-architecture-copilot',
                            name: RouteNames.AdministratorSettingsJobArchitectureCopilot,
                            component: async () => import('./views/settings/jobArchitecture/copilot/CopilotSettings.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                        },
                        {
                            path: 'currency-group',
                            name: RouteNames.AdministratorCurrencyGroup,
                            component: async () => import('./views/settings/CurrencyGroup.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                        },
                        {
                            path: 'works',
                            name: RouteNames.AdministratorWorks,
                            component: async () => import('./views/settings/Works.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                        },
                        {
                            path: 'salary-criteria-template',
                            name: RouteNames.AdministratorSalaryCriteriaTemplate,
                            component: async () => import('./views/settings/SalaryCriteriaTemplate.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                        },
                        {
                            path: 'statistical-code',
                            name: RouteNames.AdministratorStatisticalCode,
                            component: async () => import('./views/settings/StatisticalCode.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                        },
                        {
                            path: 'sso-settings',
                            name: RouteNames.AdministratorSSOSettings,
                            component: async () => import('./views/settings/SSOSettings.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                        },
                        {
                            path: 'customer-information',
                            name: RouteNames.AdministratorCustomerInforfmation,
                            component: async () => import('./views/settings/CustomerInformation.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                        },
                        {
                            path: 'role-access',
                            name: RouteNames.AdministratorRoleAccess,
                            component: async () => import('./views/settings/RoleAccess.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                        },
                        {
                            path: 'user-provisioning',
                            beforeEnter: (): { name: string } | undefined => {
                                const { activeFlags } = useLabsStore();
                                if (!activeFlags.includes('SCIM')) {
                                    return { name: RouteNames.AdministratorSettings };
                                }
                                return undefined;
                            },
                            children: [
                                {
                                    path: '',
                                    name: RouteNames.AdministratorUserProvisioning,
                                    component: async () => import('./views/settings/UserProvisioning.vue'),
                                    meta: {
                                        requiresAuth: true,
                                        userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                                    },
                                    beforeEnter: (): { name: string } | undefined => {
                                        const { scimSettings } = useSettings();
                                        if (scimSettings.value?.hasGroups === true || scimSettings.value?.hasUserRoles === true) {
                                            return { name: RouteNames.AdministratorUserProvisioningGrid };
                                        }
                                        return undefined;
                                    },
                                },
                                {
                                    path: 'list',
                                    name: RouteNames.AdministratorUserProvisioningGrid,
                                    component: async () => import('./views/settings/scim/UserProvisioningGrid.vue'),
                                    meta: {
                                        requiresAuth: true,
                                        userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                                    },
                                },
                            ],
                        },
                        {
                            path: 'api-keys',
                            beforeEnter: (): { name: string } | undefined => {
                                const { activeFlags } = useLabsStore();
                                const { enableAnalysis } = usePackage();
                                if (!activeFlags.includes('apiKeyManagement') || enableAnalysis.value !== true) {
                                    return { name: RouteNames.AdministratorSettings };
                                }
                                return undefined;
                            },
                            children: [
                                {
                                    path: '',
                                    name: RouteNames.AdministratorApiKeys,
                                    component: async () => import('./views/settings/apiKeys/ApiKeys.vue'),
                                    meta: {
                                        requiresAuth: true,
                                        userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                                    },
                                },
                                {
                                    path: ':id',
                                    name: RouteNames.AdministratorApiKeyDetail,
                                    component: async () => import('./views/settings/apiKeys/ApiKeyDetail.vue'),
                                    meta: {
                                        requiresAuth: true,
                                        userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                                    },
                                },
                            ],
                        },
                        {
                            path: 'global-pea-settings',
                            name: RouteNames.AdministratorGlobalPEASettings,
                            component: async () => import('./views/settings/GlobalPEASettings.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                        },
                        {
                            path: 'union',
                            name: RouteNames.AdministratorUnion,
                            component: async () => import('./views/settings/Union.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                        },
                        {
                            path: 'question-template',
                            name: RouteNames.AdministratorQuestionTemplate,
                            component: async () => import('./views/settings/QuestionTemplate.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                        },
                        {
                            path: 'involve-settings',
                            name: RouteNames.AdministratorInvolveSettings,
                            beforeEnter: (): { name: string } | undefined => {
                                const { hidesInvolveSettingsPage } = useSettings();

                                if (hidesInvolveSettingsPage.value) {
                                    return { name: RouteNames.AdministratorGlobalOverviewV2 };
                                }
                                return undefined;
                            },
                            component: async () => import('./views/settings/InvolveSettings.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                        },
                        {
                            path: 'country-settings/:countryCode/:legalEntityId?/employee-portal',
                            name: RouteNames.AdministratorCountryEmployeePortal,
                            beforeEnter: (): { name: string } | undefined => {
                                const { enableInvolveSettingsPage } = usePackage();
                                const { hidesInvolveSettingsPage } = useSettings();

                                if (enableInvolveSettingsPage.value !== true) {
                                    return { name: RouteNames.AdministratorSettings };
                                }
                                if (hidesInvolveSettingsPage.value) {
                                    return { name: RouteNames.AdministratorGlobalOverviewV2 };
                                }
                                return undefined;
                            },
                            component: async () => import('./views/settings/InvolveSettings.vue'),
                            props: route => ({
                                countryCode: route.params.countryCode,
                                legalEntityId: typeof route.params.legalEntityId === 'string' && route.params.legalEntityId !== '' ? route.params.legalEntityId : undefined,
                            }),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                        },
                    ]
                },
                { // Analytics
                    path: 'analytics',
                    component: async () => import('./views/analytics/Analytics.vue'),
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                    },
                    children: [
                        {
                            path: '',
                            name: RouteNames.AdministratorAnalyticsDashboard,
                            component: async () => import('./views/analytics/AnalyticsDashboard.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            }
                        },
                        {
                            path: 'analyze/:id?&:scopeType?',
                            name: RouteNames.AdministratorAnalytics,
                            component: async () => import('./views/analytics/analyze/AnalyticsAnalyze.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                        },
                        {
                            path: 'report/:id?&:scopeType?',
                            name: RouteNames.AdministratorAnalyticsReport,
                            component: async () => import('./views/analytics/reports/AnalyticsReports.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            }
                        },
                    ]
                },
                {
                    path: 'create-employee',
                    name: RouteNames.AdministatorCreateEmployee,
                    component: async () => import('./views/baseRegistry/createEmployee/CreateEmployee.vue'),
                    props: true,
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                    },
                },
                { // Data import
                    path: 'import',
                    component: async () => import('./views/Import.vue'),
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                    },
                    children: dataImportRoutes
                },
                { // Salary review
                    path: 'salary-review',
                    component: async () => import('./views/SalaryReview.vue'),
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                    },
                    children: [
                        {
                            path: '',
                            name: RouteNames.AdministratorSalaryReviewDashboard,
                            component: async () => import('./views/salaryReview/SalaryReviewDashboard.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            }
                        },
                        {
                            path: 'create/:id',
                            name: RouteNames.AdministratorCreateSalaryReview,
                            component: async () => import('./views/salaryReview/CreateSalaryReview.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                        },
                        {
                            path: ':id',
                            component: async () => import('./views/salaryReview/SalaryReviewView.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                            children: [
                                {
                                    path: '',
                                    name: RouteNames.AdministratorSalaryReviewOverview,
                                    component: async () => import('./views/salaryReview/SalaryReviewOverview.vue'),
                                    props: route => ({ id: route.params.id, employeeId: route.params.employeeId }),
                                    meta: {
                                        requiresAuth: true,
                                        userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                                    },
                                },
                                {
                                    path: 'edit-employee/:employeeId/:employeeSyncFailed',
                                    name: RouteNames.AdministratorSalaryReviewEditEmployeeView,
                                    component: async () => import('./views/salaryReview/SalaryReviewEditEmployeeView.vue'),
                                    props: route => ({
                                        id: route.params.id,
                                        employeeId: route.params.employeeId,
                                        employeeSyncFailed: (route.params.employeeSyncFailed === 'true')
                                    }),
                                    meta: {
                                        requiresAuth: true,
                                        userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                                    },
                                },
                                {
                                    path: 'criteria',
                                    name: RouteNames.AdministratorSalaryReviewCritera,
                                    component: async () => import('./views/salaryReview/SalaryReviewCriterias.vue'),
                                    props: route => ({ id: route.params.id }),
                                    meta: {
                                        requiresAuth: true,
                                        userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                                    },
                                },
                                {
                                    path: 'budget',
                                    name: RouteNames.AdministratorSalaryReviewBudget,
                                    component: async () => import('./views/salaryReview/SalaryReviewBudgetView.vue'),
                                    props: route => ({ id: route.params.id }),
                                    meta: {
                                        requiresAuth: true,
                                        userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                                    },
                                },
                                {
                                    path: 'result',
                                    name: RouteNames.AdministratorSalaryReviewResult,
                                    component: async () => import('./views/salaryReview/SalaryReviewResult.vue'),
                                    props: route => ({ id: route.params.id, employeeId: route.params.employeeId }),
                                    meta: {
                                        requiresAuth: true,
                                        userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                                    },
                                },
                                {
                                    path: 'employee/:employeeId/:name/:position/:organisation?',
                                    name: RouteNames.AdministratorSalaryReviewResultEmployeeView,
                                    component: async () => import('./views/salaryReview/SalaryReviewResultEmployeeView.vue'),
                                    props: route => ({
                                        id: route.params.id,
                                        employeeId: route.params.employeeId,
                                        name: route.params.name,
                                        position: route.params.position,
                                        organisation: route.params.organisation,
                                        salaryType: route.params.salaryType,
                                        currency: route.params.currency,
                                    }),
                                    meta: {
                                        requiresAuth: true,
                                        userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                                    }
                                }
                            ]
                        },

                    ]
                },
                { // Discussions
                    path: 'discussions',
                    component: async () => import('./views/Discussion.vue'),
                    props: true,
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                    },
                    children: [
                        {
                            path: '',
                            name: RouteNames.AdministratorDiscussionDashboard,
                            component: async () => import('./views/discussions/DiscussionDashboard.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            }
                        },
                        {
                            path: ':id',
                            component: async () => import('./views/discussions/DiscussionView.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                            children: [
                                {
                                    path: '',
                                    name: RouteNames.AdministratorDiscussionOverview,
                                    component: async () => import('./views/discussions/DiscussionOverview.vue'),
                                    meta: {
                                        requiresAuth: true,
                                        userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                                    },
                                },
                                {
                                    path: 'questionnaire',
                                    name: RouteNames.AdministratorDiscussionQuestionnaire,
                                    component: async () => import('./views/discussions/DiscussionQuestionnaire.vue'),
                                    meta: {
                                        requiresAuth: true,
                                        userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                                    },
                                },
                                {
                                    path: 'connected-employments/:organisationId/:manager',
                                    name: RouteNames.AdministratorExistingDiscussionEmployments,
                                    component: async () => import('./views/discussions/DiscussionEmploymentsView.vue'),
                                    props: route => ({
                                        id: route.params.id,
                                        organisationId: route.params.organisationId,
                                        manager: route.params.manager
                                    }),
                                    meta: {
                                        requiresAuth: true,
                                        userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                                    }
                                }
                            ]
                        },
                        {
                            path: 'create/:id',
                            name: RouteNames.AdministratorCreateDiscussion,
                            component: async () => import('./views/discussions/CreateDiscussionView.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                        }
                    ]
                },
                { // Pay Equity Audit
                    path: 'pay-equity-audit',
                    component: async () => import('./views/PayEquityAudit.vue'),
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                    },
                    children: [
                        {
                            path: '',
                            name: RouteNames.AdministratorPayEquityAuditDashboard,
                            component: async () => import('./views/payEquityAudit/Dashboard.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [
                                    Role.Administrator,
                                    Role.Root,
                                    Role.SystemAdministrator,
                                    Role.Consultant,
                                ]
                            }
                        },
                        {
                            path: 'onboarding/:id?',
                            name: RouteNames.AdministratorPayEquityAuditOnboarding,
                            component: async () => import('./views/payEquityAudit/onboarding/PayEquityAuditOnboarding.vue'),
                            props: (route) => ({
                                id: route.params.id,
                                legalEntityId: route.query.legalEntity,
                                countryCode: route.query.countryCode,
                            }),
                            meta: {
                                requiresAuth: true,
                                userTypes: [
                                    Role.Administrator,
                                    Role.Root,
                                    Role.SystemAdministrator,
                                    Role.Consultant,
                                ]
                            }
                        },
                        {
                            path: ':id',
                            alias: 'mappings/:id',
                            component: async () => import('./views/payEquityAudit/PayEquityAudit.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [
                                    Role.Administrator,
                                    Role.Root,
                                    Role.SystemAdministrator,
                                    Role.Consultant,
                                ],
                            },
                            children: [
                                {
                                    path: 'pay-equity-audit-overview',
                                    name: RouteNames.AdministratorPayEquityAuditOverview,
                                    component: async () => import('./views/payEquityAudit/overview/PayEquityAuditOverview.vue'),
                                    meta: {
                                        requiresAuth: true,
                                        userTypes: [
                                            Role.Administrator,
                                            Role.Root,
                                            Role.SystemAdministrator,
                                            Role.Consultant,
                                        ]
                                    }
                                },
                                {
                                    path: 'prepare',
                                    component: async () => import('./views/mappings/prepare/MappingsPrepareView.vue'),
                                    meta: {
                                        requiresAuth: true,
                                        userTypes: [
                                            Role.Administrator,
                                            Role.Root,
                                            Role.SystemAdministrator,
                                            Role.Consultant,
                                        ],
                                    },
                                    children: [
                                        {
                                            path: '',
                                            name: RouteNames.AdministratorMappingPrepare,
                                            component: async () => import('./views/mappings/prepare/MappingsPrepare.vue'),
                                            meta: {
                                                requiresAuth: true,
                                                userTypes: [
                                                    Role.Administrator,
                                                    Role.Root,
                                                    Role.SystemAdministrator,
                                                    Role.Consultant,
                                                ],
                                            },
                                        },
                                        {
                                            path: 'adjusted-salary',
                                            name: RouteNames.AdministratorMappingPrepareAdjustedSalary,
                                            component: async () => import('./views/mappings/prepare/MappingsPrepareAdjustedSalary.vue'),
                                            beforeEnter: (to: RouteLocationNormalized): { name: string; params: RouteLocationNormalized['params'] } => {
                                                return { name: RouteNames.AdministratorPayPhilosophyModelInsights, params: to.params };
                                            },
                                            meta: {
                                                requiresAuth: true,
                                                userTypes: [
                                                    Role.Administrator,
                                                    Role.Root,
                                                    Role.SystemAdministrator,
                                                    Role.Consultant,
                                                ],
                                            }
                                        }
                                    ]
                                },
                                {
                                    path: 'pay-equity-audit-organize',
                                    component: async () => import('./views/payEquityAudit/organize/PayEquityAuditOrganizeView.vue'),
                                    meta: {
                                        requiresAuth: true,
                                        userTypes: [
                                            Role.Administrator,
                                            Role.Root,
                                            Role.SystemAdministrator,
                                            Role.Consultant,
                                        ],
                                    },
                                    children: [
                                        {
                                            path: '',
                                            name: RouteNames.AdministratorPayEquityAuditOrganizeGroup,
                                            component: async () => import('./views/payEquityAudit/organize/PayEquityAuditOrganizeGroup.vue'),
                                            meta: {
                                                requiresAuth: true,
                                                userTypes: [
                                                    Role.Administrator,
                                                    Role.Root,
                                                    Role.SystemAdministrator,
                                                    Role.Consultant,
                                                ],
                                            },
                                        },
                                        {
                                            path: 'work-valuation',
                                            name: RouteNames.AdministratorPayEquityAuditOrganizeWorkValuation,
                                            component: async () => import('./views/payEquityAudit/organize/PayEquityAuditOrganizeWorkValuation.vue'),
                                            meta: {
                                                requiresAuth: true,
                                                userTypes: [
                                                    Role.Administrator,
                                                    Role.Root,
                                                    Role.SystemAdministrator,
                                                    Role.Consultant,
                                                ],
                                            }
                                        }
                                    ]
                                },
                                {
                                    path: 'analyze',
                                    component: async () => import('./views/mappings/analyze/MappingsAnalyzeView.vue'),
                                    meta: {
                                        requiresAuth: true,
                                        userTypes: [
                                            Role.Administrator,
                                            Role.Root,
                                            Role.SystemAdministrator,
                                            Role.Consultant,
                                        ],
                                    },
                                    children: [
                                        {
                                            path: '',
                                            name: RouteNames.AdministratorMappingAnalyzeEqualWorks,
                                            component: async () => import('./views/mappings/analyze/MappingsAnalyzeEqualWorks.vue'),
                                            props: true,
                                            meta: {
                                                requiresAuth: true,
                                                userTypes: [
                                                    Role.Administrator,
                                                    Role.Root,
                                                    Role.SystemAdministrator,
                                                    Role.Consultant,
                                                ],
                                            },
                                        },
                                        {
                                            path: 'equivalent-works',
                                            name: RouteNames.AdministratorMappingAnalyzeEquivalentWorks,
                                            component: async () => import('./views/mappings/analyze/MappingsAnalyzeEquivalentWorks.vue'),
                                            meta: {
                                                requiresAuth: true,
                                                userTypes: [
                                                    Role.Administrator,
                                                    Role.Root,
                                                    Role.SystemAdministrator,
                                                    Role.Consultant,
                                                ],
                                            },
                                        },
                                        {
                                            path: 'equivalent-works-v2',
                                            name: RouteNames.AdministratorMappingAnalyzeEquivalentWorksV2,
                                            component: async () => import('./views/mappings/analyze/AnalyzeLegacyEquivalentWorks.vue'),
                                            meta: {
                                                requiresAuth: true,
                                                userTypes: [
                                                    Role.Administrator,
                                                    Role.Root,
                                                    Role.SystemAdministrator,
                                                    Role.Consultant,
                                                ],
                                            },
                                        },
                                    ]
                                },
                                // PEA New Analysis
                                {
                                    path: 'analyze-v2',
                                    component: async () => import('./views/payEquityAudit/analyzeV2/AnalyzeView.vue'),
                                    meta: {
                                        requiresAuth: true,
                                        userTypes: [
                                            Role.Administrator,
                                            Role.Root,
                                            Role.SystemAdministrator,
                                            Role.Consultant,
                                        ],
                                    },
                                    children: [
                                        {
                                            path: '',
                                            component: async () => import('./views/payEquityAudit/analyzeV2/AnalyzeEqualJobsList.vue'),
                                            name: RouteNames.AdministratorPayEquityAuditAnalyzeEqualJobsList,
                                            meta: {
                                                requiresAuth: true,
                                                userTypes: [
                                                    Role.Administrator,
                                                    Role.Root,
                                                    Role.SystemAdministrator,
                                                    Role.Consultant,
                                                ],
                                            },
                                        },
                                        {
                                            path: 'equivalent-works',
                                            component: async () => import('./views/mappings/analyze/MappingsAnalyzeEquivalentWorks.vue'),
                                            name: RouteNames.AdministratorPayEquityAuditAnalyzeEquivalentWorks,
                                            meta: {
                                                requiresAuth: true,
                                                userTypes: [
                                                    Role.Administrator,
                                                    Role.Root,
                                                    Role.SystemAdministrator,
                                                    Role.Consultant,
                                                ],
                                            },
                                        },
                                        {
                                            path: ':jobId/:analysisType?',
                                            component: async () => import('./views/payEquityAudit/analyzeV2/Analyze.vue'),
                                            meta: {
                                                requiresAuth: true,
                                                userTypes: [
                                                    Role.Administrator,
                                                    Role.Root,
                                                    Role.SystemAdministrator,
                                                    Role.Consultant,
                                                ],
                                            },
                                            props: (route: RouteLocationNormalized): _RouteRecordProps => {
                                                return {
                                                    jobId: route.params.jobId,
                                                    analysisType: isAnalysisType(route.params.analysisType) ? route.params.analysisType : 'predicted-salary',
                                                };
                                            },
                                            name: RouteNames.AdministratorPayEquityAuditAnalyzeWorksV2,
                                        },
                                    ]
                                },
                                {
                                    path: 'pay-equity-audit-report',
                                    name: RouteNames.AdministratorPayEquityAuditReport,
                                    component: async () => import('./views/payEquityAudit/report/PayEquityAuditReport.vue'),
                                    meta: {
                                        requiresAuth: true,
                                        userTypes: [
                                            Role.Administrator,
                                            Role.Root,
                                            Role.SystemAdministrator,
                                            Role.Consultant,
                                        ]
                                    }
                                },
                                {
                                    path: 'model-insights',
                                    name: RouteNames.AdministratorPayPhilosophyModelInsights,
                                    component: async () => import('./views/payEquityAudit/modelInsights/PayPhilosophyModelInsights.vue'),
                                    props: true,
                                    meta: {
                                        requiresAuth: true,
                                        userTypes: [
                                            Role.Administrator,
                                            Role.Root,
                                            Role.SystemAdministrator,
                                            Role.Consultant,
                                        ]
                                    }
                                }
                            ]
                        },
                    ]
                },
                { // Friends
                    path: 'friends',
                    name: RouteNames.AdministratorFriends,
                    component: async () => import('./views/Friends.vue'),
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.Root, Role.SystemAdministrator]
                    },
                },
                {
                    path: 'external-job-architecture',
                    component: async () => import('./views/externalJobArchitecture/ExternalJobArchitecture.vue'),
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                    },
                    children: [
                        {
                            path: '',
                            name: RouteNames.AdministratorExternalJobArchitectureOverview,
                            component: async () => import('./views/externalJobArchitecture/ExternalOverview.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                        },
                        {
                            path: '',
                            name: RouteNames.AdministratorExternalJobArchitecturePayRangeV2,
                            component: async () => import('./views/externalJobArchitecture/payRangesV2/ExternalPayRanges.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                        },
                    ]
                },
                {
                    path: 'job-architecture-v2',
                    component: async () => import('./views/jobArchitecture/JobArchitecture.vue'),
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                    },
                    children: jobArchitectureRoutes
                },
                {
                    path: 'job-architecture-introduction',
                    name: RouteNames.AdministratorJobArchitectureIntroduction,
                    component: async () => import('./views/jobArchitecture/introduction/JobArchitectureIntroduction.vue'),
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                    },
                    children: [
                        {
                            path: 'select-type',
                            name: RouteNames.AdministratorJobArchitectureSelectType,
                            component: async () => import('./views/jobArchitecture/introduction/JobArchitectureSelectType.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                        },
                        {
                            path: 'create-job-profiles',
                            name: RouteNames.AdministratorJobArchitectureCreateJobProfiles,
                            component: async () => import('./views/jobArchitecture/manageJobProfiles/CreateJobProfiles.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                        },
                        {
                            path: '/create-job-profile',
                            name: RouteNames.AdministratorJobArchitectureCreateJobProfile,
                            component: async () => import('@/views/jobArchitecture/manageJobProfiles/CreateJobProfile.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                        },
                        {
                            path: 'employee-copilot-settings',
                            name: RouteNames.AdministratorJobArchitectureEmployeeCopilotSettingsOnCreateJobArchitecture,
                            component: async () => import('@/views/jobArchitecture/introduction/JobArchitectureCopilot.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                            },
                        },
                    ]
                },
            ]
        },
        { // Operational Administrator
            path: '/operationaladministrator',
            component: async () => import('./views/baseView/OperationalAdministrator.vue'),
            meta: {
                requiresAuth: true,
                userTypes: [Role.OperationalAdministrator]
            },
            children: [
                {
                    path: 'welcome-to-sysarb',
                    name: RouteNames.OperationalAdministratorWelcomeToSysarb,
                    component: async () => import('./views/WelcomeToSysarb.vue'),
                    props: true,
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.OperationalAdministrator]
                    },
                },
                {
                    path: 'global-overview-v2',
                    name: RouteNames.OperationalAdministratorGlobalOverviewV2,
                    component: async () => import('./views/globalOverviewV2/LandingView.vue'),
                    props: route => ({
                        salaryType: typeof route.query.salaryType === 'string' ? route.query.salaryType : undefined,
                        legalEntity: typeof route.query.legalEntity === 'string' ? route.query.legalEntity : undefined,
                        countryCode: typeof route.query.countryCode === 'string' ? route.query.countryCode : undefined,
                    }),
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.OperationalAdministrator]
                    },
                },
                {
                    path: 'pay-equity-audit/dashboard',
                    name: RouteNames.OperationalAdministratorPayEquityAuditDashboard,
                    component: async () => import('./views/payEquityAudit/Dashboard.vue'),
                    props: true,
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.OperationalAdministrator]
                    },
                },
                {
                    path: 'pay-equity-audit/onboarding/:id?',
                    name: RouteNames.OperationalAdministratorPayEquityAuditOnboarding,
                    component: async () => import('./views/payEquityAudit/onboarding/PayEquityAuditOnboarding.vue'),
                    props: (route) => ({
                        id: route.params.id,
                        legalEntityId: route.query.legalEntity,
                        countryCode: route.query.countryCode,
                    }),
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.OperationalAdministrator]
                    },
                },
                {
                    path: 'pay-equity-audit/:id',
                    alias: 'mappings/:id',
                    component: async () => import('./views/payEquityAudit/PayEquityAudit.vue'),
                    props: true,
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.OperationalAdministrator]
                    },
                    children: [
                        {
                            path: 'pay-equity-audit-overview',
                            name: RouteNames.OperationalAdministratorMappings,
                            component: async () => import('./views/payEquityAudit/overview/PayEquityAuditOverview.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.OperationalAdministrator]
                            },
                        },
                        {
                            path: 'prepare',
                            name: RouteNames.OperationalAdministratorMappingPrepare,
                            component: async () => import('./views/mappings/prepare/MappingsPrepare.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.OperationalAdministrator]
                            }
                        },
                        {
                            path: 'prepare',
                            component: async () => import('./views/mappings/prepare/MappingsPrepareView.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.OperationalAdministrator]
                            },
                            children: [
                                {
                                    path: '',
                                    name: RouteNames.OperationalAdministratorMappingPrepare,
                                    component: async () => import('./views/mappings/prepare/MappingsPrepare.vue'),
                                    props: true,
                                    meta: {
                                        requiresAuth: true,
                                        userTypes: [Role.OperationalAdministrator]
                                    },
                                },
                                {
                                    path: 'adjusted-salary',
                                    name: RouteNames.OperationalAdministratorMappingPrepareAdjustedSalary,
                                    component: async () => import('./views/mappings/prepare/MappingsPrepareAdjustedSalary.vue'),
                                    props: true,
                                    beforeEnter: (to: RouteLocationNormalized): { name: string; params: RouteLocationNormalized['params'] } => {
                                        return { name: RouteNames.OperationalAdministratorPayPhilosophyModelInsights, params: to.params };
                                    },
                                    meta: {
                                        requiresAuth: true,
                                        userTypes: [Role.OperationalAdministrator]
                                    }
                                }
                            ]
                        },
                        {
                            path: 'organise/group',
                            name: RouteNames.OperationalAdministratorPayEquityAuditOrganizeGroup,
                            component: async () => import('./views/payEquityAudit/organize/PayEquityAuditOrganizeGroup.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [
                                    Role.OperationalAdministrator
                                ],
                            },
                        },
                        {
                            path: 'organise/work-valuation',
                            name: RouteNames.OperationalAdministratorPayEquityAuditOrganizeWorkValuation,
                            component: async () => import('./views/payEquityAudit/organize/PayEquityAuditOrganizeWorkValuation.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [
                                    Role.OperationalAdministrator
                                ],
                            }
                        },
                        {
                            path: 'analyze/equal-works',
                            name: RouteNames.OperationalAdministratorMappingAnalyzeEqualWorks,
                            component: async () => import('./views/mappings/analyze/MappingsAnalyzeEqualWorks.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.OperationalAdministrator]
                            },
                        },
                        {
                            path: 'analyze/equivalent-works',
                            name: RouteNames.OperationalAdministratorMappingAnalyzeEquivalentWorks,
                            component: async () => import('./views/mappings/analyze/MappingsAnalyzeEquivalentWorks.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.OperationalAdministrator]
                            },
                        },
                        {
                            path: 'analyze-v2/equivalent-works',
                            name: RouteNames.OperationalAdministratorPayEquityAuditAnalyzeEquivalentWorks,
                            component: async () => import('./views/mappings/analyze/MappingsAnalyzeEquivalentWorks.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.OperationalAdministrator]
                            },
                        },
                        {
                            path: 'documentation/export',
                            name: RouteNames.OperationalAdministratorPayEquityAuditExport,
                            component: async () => import('./views/payEquityAudit/report/PayEquityAuditReport.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.OperationalAdministrator]
                            },
                        },
                        {
                            path: 'model-insights',
                            name: RouteNames.OperationalAdministratorPayPhilosophyModelInsights,
                            component: async () => import('./views/payEquityAudit/modelInsights/PayPhilosophyModelInsights.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.OperationalAdministrator]
                            },
                        },
                        {
                            path: 'analyze-v2',
                            component: async () => import('./views/payEquityAudit/analyzeV2/AnalyzeEqualJobsList.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.OperationalAdministrator],
                            },
                            name: RouteNames.OperationalAdministratorPayEquityAuditAnalyzeEqualJobsList,
                        },
                        {
                            path: 'analyze-v2/:jobId/:analysisType?',
                            component: async () => import('./views/payEquityAudit/analyzeV2/Analyze.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.OperationalAdministrator],
                            },
                            props: (route: RouteLocationNormalized): _RouteRecordProps => {
                                return {
                                    jobId: route.params.jobId,
                                    analysisType: isAnalysisType(route.params.analysisType) ? route.params.analysisType : 'predicted-salary',
                                };
                            },
                            name: RouteNames.OperationalAdministratorPayEquityAuditAnalyzeWorksV2,
                        },
                    ]
                },
                {
                    path: 'job-architecture',
                    component: async () => import('./views/jobArchitecturePreview/JobArchitecturePreview.vue'),
                    props: true,
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.OperationalAdministrator]
                    },
                    children: [
                        {
                            path: '',
                            name: RouteNames.OperationalAdministratorJobArchitectureOverview,
                            component: async () => import('./views/jobArchitecturePreview/overview/Overview.vue'),
                        },
                    ]
                },
                {
                    path: 'external-job-architecture',
                    component: async () => import('./views/externalJobArchitecture/preview/ExternalJobArchitecturePreview.vue'),
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.OperationalAdministrator]
                    },
                    children: [
                        {
                            path: '',
                            name: RouteNames.OperationalAdministratorExternalJobArchitectureOverview,
                            component: async () => import('./views/externalJobArchitecture/ExternalOverview.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.OperationalAdministrator]
                            },
                        },
                    ]
                },
            ]
        },
        { // Salary Survey Analyst
            path: '/analyst',
            component: async () => import('./views/baseView/Analyst.vue'),
            meta: {
                requiresAuth: true,
                userTypes: [Role.SalarySurveyAnalyst]
            },
            children: [
                {
                    path: 'welcome-to-sysarb',
                    name: RouteNames.AnalystWelcomeToSysarb,
                    component: async () => import('./views/WelcomeToSysarb.vue'),
                    props: true,
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.SalarySurveyAnalyst]
                    },
                },
                {
                    path: 'pay-equity-audit/dashboard',
                    name: RouteNames.AnalystPayEquityAuditDashboard,
                    component: async () => import('./views/payEquityAudit/Dashboard.vue'),
                    props: true,
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.SalarySurveyAnalyst]
                    },
                },
                {
                    path: 'global-overview-v2',
                    name: RouteNames.AnalystGlobalOverviewV2,
                    component: async () => import('./views/globalOverviewV2/LandingView.vue'),
                    props: route => ({
                        salaryType: typeof route.query.salaryType === 'string' ? route.query.salaryType : undefined,
                        legalEntity: typeof route.query.legalEntity === 'string' ? route.query.legalEntity : undefined,
                        countryCode: typeof route.query.countryCode === 'string' ? route.query.countryCode : undefined,
                    }),
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.SalarySurveyAnalyst]
                    },
                },
                {
                    path: 'pay-equity-audit/:id',
                    alias: 'mappings/:id',
                    component: async () => import('./views/payEquityAudit/PayEquityAudit.vue'),
                    props: true,
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.SalarySurveyAnalyst]
                    },
                    children: [
                        {
                            path: 'pay-equity-audit-overview',
                            name: RouteNames.AnalystMappings,
                            component: async () => import('./views/payEquityAudit/overview/PayEquityAuditOverview.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.SalarySurveyAnalyst]
                            },
                        },
                        {
                            path: 'organise/group',
                            name: RouteNames.AnalystPayEquityAuditOrganizeGroup,
                            component: async () => import('./views/payEquityAudit/organize/PayEquityAuditOrganizeGroup.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.SalarySurveyAnalyst]
                            },
                        },
                        {
                            path: 'analyze',
                            component: async () => import('./views/mappings/analyze/MappingsAnalyzeView.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.SalarySurveyAnalyst],
                            },
                            children: [
                                {
                                    path: '',
                                    name: RouteNames.AnalystMappingAnalyzeEqualWorks,
                                    component: async () => import('./views/mappings/analyze/MappingsAnalyzeEqualWorks.vue'),
                                    props: true,
                                    meta: {
                                        requiresAuth: true,
                                        userTypes: [Role.SalarySurveyAnalyst]
                                    },
                                },
                                {
                                    path: 'equivalent-works',
                                    name: RouteNames.AnalystMappingAnalyzeEquivalentWorks,
                                    component: async () => import('./views/mappings/analyze/MappingsAnalyzeEquivalentWorks.vue'),
                                    props: true,
                                    meta: {
                                        requiresAuth: true,
                                        userTypes: [Role.SalarySurveyAnalyst]
                                    },
                                },
                            ],
                        },
                        {
                            path: 'analyze-v2',
                            component: async () => import('./views/payEquityAudit/analyzeV2/AnalyzeView.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.SalarySurveyAnalyst],
                            },
                            children: [
                                {
                                    path: '',
                                    component: async () => import('./views/payEquityAudit/analyzeV2/AnalyzeEqualJobsList.vue'),
                                    name: RouteNames.AnalystPayEquityAuditAnalyzeEqualJobsList,
                                    meta: {
                                        requiresAuth: true,
                                        userTypes: [Role.SalarySurveyAnalyst],
                                    },
                                },
                                {
                                    path: 'equivalent-works',
                                    name: RouteNames.AnalystPayEquityAuditAnalyzeEquivalentWorks,
                                    component: async () => import('./views/mappings/analyze/MappingsAnalyzeEquivalentWorks.vue'),
                                    props: true,
                                    meta: {
                                        requiresAuth: true,
                                        userTypes: [Role.SalarySurveyAnalyst],
                                    },
                                },
                                {
                                    path: ':jobId/:analysisType?',
                                    component: async () => import('./views/payEquityAudit/analyzeV2/Analyze.vue'),
                                    meta: {
                                        requiresAuth: true,
                                        userTypes: [Role.SalarySurveyAnalyst],
                                    },
                                    props: (route: RouteLocationNormalized): _RouteRecordProps => {
                                        return {
                                            jobId: route.params.jobId,
                                            analysisType: isAnalysisType(route.params.analysisType) ? route.params.analysisType : 'predicted-salary',
                                        };
                                    },
                                    name: RouteNames.AnalystPayEquityAuditAnalyzeWorksV2,
                                },
                            ],
                        },
                    ]
                },
                {
                    path: 'job-architecture',
                    component: async () => import('./views/jobArchitecturePreview/JobArchitecturePreview.vue'),
                    props: true,
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.SalarySurveyAnalyst]
                    },
                    children: [
                        {
                            path: '',
                            name: RouteNames.AnalystJobArchitectureOverview,
                            component: async () => import('./views/jobArchitecturePreview/overview/Overview.vue'),
                        },
                    ]
                },
                {
                    path: 'external-job-architecture',
                    component: async () => import('./views/externalJobArchitecture/preview/ExternalJobArchitecturePreview.vue'),
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.SalarySurveyAnalyst]
                    },
                    children: [
                        {
                            path: '',
                            name: RouteNames.AnalystExternalJobArchitectureOverview,
                            component: async () => import('./views/externalJobArchitecture/ExternalOverview.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.SalarySurveyAnalyst]
                            },
                        },
                    ]
                },
            ]
        },
        { // ReadOnly
            path: '/readonly',
            component: async () => import('./views/baseView/ReadOnly.vue'),
            meta: {
                requiresAuth: true,
                userTypes: [Role.ReadOnly]
            },
            children: [
                {
                    path: 'welcome-to-sysarb',
                    name: RouteNames.ReadOnlyWelcomeToSysarb,
                    component: async () => import('./views/WelcomeToSysarb.vue'),
                    props: true,
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.ReadOnly]
                    },
                },
                {
                    path: 'global-overview-v2',
                    name: RouteNames.ReadOnlyGlobalOverviewV2,
                    component: async () => import('./views/globalOverviewV2/LandingView.vue'),
                    props: route => ({
                        salaryType: typeof route.query.salaryType === 'string' ? route.query.salaryType : undefined,
                        legalEntity: typeof route.query.legalEntity === 'string' ? route.query.legalEntity : undefined,
                        countryCode: typeof route.query.countryCode === 'string' ? route.query.countryCode : undefined,
                    }),
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.ReadOnly]
                    },
                },
                {
                    path: 'pay-equity-audit/dashboard',
                    name: RouteNames.ReadOnlyPayEquityAuditDashboard,
                    component: async () => import('./views/payEquityAudit/Dashboard.vue'),
                    props: true,
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.ReadOnly]
                    },
                },
                {
                    path: 'pay-equity-audit/:id',
                    alias: 'mappings/:id',
                    component: async () => import('./views/payEquityAudit/PayEquityAudit.vue'),
                    props: true,
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.ReadOnly]
                    },
                    children: [
                        {
                            path: 'pay-equity-audit-overview',
                            name: RouteNames.ReadOnlyMappings,
                            component: async () => import('./views/payEquityAudit/overview/PayEquityAuditOverview.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.ReadOnly]
                            },
                        },
                        {
                            path: 'organise/work-valuation',
                            name: RouteNames.ReadOnlyPayEquityAuditOrganizeWorkValuation,
                            component: async () => import('./views/payEquityAudit/organize/PayEquityAuditOrganizeWorkValuation.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.ReadOnly]
                            },
                        },
                        {
                            path: 'analyze/equal-works',
                            name: RouteNames.ReadOnlyMappingAnalyzeEqualWorks,
                            component: async () => import('./views/mappings/analyze/MappingsAnalyzeEqualWorks.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.ReadOnly]
                            },
                        },
                        {
                            path: 'analyze/equivalent-works',
                            name: RouteNames.ReadOnlyMappingAnalyzeEquivalentWorks,
                            component: async () => import('./views/mappings/analyze/MappingsAnalyzeEquivalentWorks.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.ReadOnly]
                            },
                        },
                        {
                            path: 'analyze-v2/equivalent-works',
                            name: RouteNames.ReadOnlyPayEquityAuditAnalyzeEquivalentWorks,
                            component: async () => import('./views/mappings/analyze/MappingsAnalyzeEquivalentWorks.vue'),
                            props: true,
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.ReadOnly]
                            },
                        },
                        {
                            path: 'analyze-v2',
                            component: async () => import('./views/payEquityAudit/analyzeV2/AnalyzeEqualJobsList.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [
                                    Role.Administrator,
                                    Role.Root,
                                    Role.SystemAdministrator,
                                    Role.Consultant,
                                    Role.SalarySurveyAnalyst,
                                    Role.ReadOnly
                                ],
                            },
                            name: RouteNames.ReadOnlyPayEquityAuditAnalyzeEqualJobsList,
                        },
                        {
                            path: 'analyze-v2/:jobId/:analysisType?',
                            component: async () => import('./views/payEquityAudit/analyzeV2/Analyze.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [
                                    Role.Administrator,
                                    Role.Root,
                                    Role.SystemAdministrator,
                                    Role.Consultant,
                                    Role.SalarySurveyAnalyst,
                                    Role.ReadOnly
                                ],
                            },
                            props: (route: RouteLocationNormalized): _RouteRecordProps => {
                                return {
                                    jobId: route.params.jobId,
                                    analysisType: isAnalysisType(route.params.analysisType) ? route.params.analysisType : 'predicted-salary',
                                };
                            },
                            name: RouteNames.ReadOnlyPayEquityAuditAnalyzeWorksV2,
                        },
                    ]
                },
                {
                    path: 'job-architecture',
                    component: async () => import('./views/jobArchitecturePreview/JobArchitecturePreview.vue'),
                    props: true,
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.ReadOnly]
                    },
                    children: [
                        {
                            path: '',
                            name: RouteNames.ReadOnlyJobArchitectureOverview,
                            component: async () => import('./views/jobArchitecturePreview/overview/Overview.vue'),
                        },
                    ]
                },
                {
                    path: 'external-job-architecture',
                    component: async () => import('./views/externalJobArchitecture/preview/ExternalJobArchitecturePreview.vue'),
                    meta: {
                        requiresAuth: true,
                        userTypes: [Role.ReadOnly]
                    },
                    children: [
                        {
                            path: '',
                            name: RouteNames.ReadOnlyExternalJobArchitectureOverview,
                            component: async () => import('./views/externalJobArchitecture/ExternalOverview.vue'),
                            meta: {
                                requiresAuth: true,
                                userTypes: [Role.ReadOnly]
                            },
                        },
                    ]
                },
            ]
        },
        { // Catch-all for unknown URLs. Logged-out users go to login, logged-in users to their start page.
            path: '/:pathMatch(.*)*',
            redirect: (): RouteLocationRaw => {
                const { userId, role } = useLoginStore();
                if (userId == null) return { name: RouteNames.Login };
                return getUserTypeStartPageRoute(role?.role ?? null, usePackage());
            },
        },
    ],
    scrollBehavior(to, from, savedPosition) {
        // Will return the user back to the scroll position if using the back and forward buttons
        if (savedPosition != null) {
            return savedPosition;
        }
        return { left: 0, top: 0 };
    }
};

export {
    routerOptions
};
